import type { Metric } from "web-vitals";

/**
 * The five Core Web Vitals the RUM service accepts. Mirrors the server's
 * `WEB_VITAL_METRIC_NAMES` (web-vitals/src/domain/metric.ts). `web-vitals` v4
 * can also report `FID`, which the service does not accept — such metrics are
 * dropped before they ever enter the collector.
 */
export const WEB_VITAL_METRIC_NAMES = [
  "LCP",
  "INP",
  "CLS",
  "FCP",
  "TTFB",
] as const;
export type WebVitalMetricName = (typeof WEB_VITAL_METRIC_NAMES)[number];

export type WebVitalRating = "good" | "needs-improvement" | "poor";

/**
 * One report-revision, shaped to the server's Effect `WebVitalReportInput`
 * schema (web-vitals/src/domain/metric.ts). The backend derives `partnerId`,
 * `device`, and `country` itself, so the collector never sends them.
 */
export interface WebVitalReport {
  /** Native web-vitals metric `id` — the stable per-instance `reportId`. */
  id: string;
  /** Monotonically increasing per `id`: 0 on first emit, +1 on each re-emit. */
  revision: number;
  name: WebVitalMetricName;
  /** The measured sample (never the delta). */
  value: number;
  rating: WebVitalRating;
  delta: number;
  navigationType: string;
  /** Collector-matched route pattern (dynamic segments collapsed). Frozen per `id`. */
  route: string;
  /** `window.location.pathname` only — no query/fragment/PII. Frozen per `id`. */
  path: string;
  /** Client epoch ms, stamped once at metric creation. Frozen per `id`. */
  ts: number;
}

/** The dimensional identity of a metric instance, captured once and reused. */
export interface FrozenContext {
  ts: number;
  route: string;
  path: string;
}

/**
 * Per-`id` accumulator. `ts`/`route`/`path` are frozen on first sight
 * (collector obligations 2 & 4, §6.1); only `value`/`delta`/`rating`/
 * `navigationType` are allowed to refine across web-vitals callbacks.
 */
interface MetricState {
  id: string;
  name: WebVitalMetricName;
  context: FrozenContext;
  value: number;
  delta: number;
  rating: WebVitalRating;
  navigationType: string;
  /** Next revision to assign; advances only when a report is actually emitted. */
  revision: number;
  /** The `value` most recently emitted, or `undefined` if never emitted. */
  lastSentValue: number | undefined;
}

function isWebVitalMetricName(name: string): name is WebVitalMetricName {
  return (WEB_VITAL_METRIC_NAMES as readonly string[]).includes(name);
}

/**
 * Collapse the obviously-dynamic segments of a pathname into a `:id`
 * placeholder so routes stay low-cardinality (design §5.3). This is a
 * conservative heuristic — numeric ids, UUIDs, and long hex/hash segments —
 * not a router-template match, so word-slug dynamic segments (e.g. Prismic
 * `[uid]`) remain literal. The backend byte-guards and folds any residual
 * high-cardinality route into `__OTHER__`, so an imperfect pattern is safe.
 */
export function deriveRoute(pathname: string): string {
  const UUID =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const collapsed = pathname
    .split("/")
    .map((segment) => {
      if (segment === "") return segment;
      if (/^\d+$/.test(segment)) return ":id";
      if (UUID.test(segment)) return ":id";
      if (/^[0-9a-f]{16,}$/i.test(segment)) return ":id";
      return segment;
    })
    .join("/");
  return collapsed === "" ? "/" : collapsed;
}

/**
 * Accumulates web-vitals metrics and emits batched, revisioned reports on
 * demand. Framework-agnostic and side-effect-free apart from the injected
 * `send` — the React wiring (lifecycle listeners, metric registration) lives
 * in `web-vitals.tsx`.
 *
 * Delivery model (§6.1): metrics are recorded as web-vitals reports them, and
 * `flush()` is called at each `visibilitychange → hidden` (interim) and at
 * `pagehide` (final). Each flush emits only the metrics that are new or whose
 * value refined since their last emit, each at the next monotonic revision —
 * so redundant flushes cost nothing and the backend's highest-revision-per-`id`
 * dedupe makes the beacons order-independent.
 */
export class WebVitalsCollector {
  private readonly states = new Map<string, MetricState>();

  constructor(
    private readonly send: (reports: WebVitalReport[]) => void,
    private readonly context: () => FrozenContext,
  ) {}

  /** Record a web-vitals callback, freezing context on first sight of an `id`. */
  record(metric: Metric): void {
    if (!isWebVitalMetricName(metric.name)) return;

    const existing = this.states.get(metric.id);
    if (existing) {
      // Only the sample may change; the dimensional identity stays frozen.
      existing.value = metric.value;
      existing.delta = metric.delta;
      existing.rating = metric.rating;
      existing.navigationType = metric.navigationType;
      return;
    }

    this.states.set(metric.id, {
      id: metric.id,
      name: metric.name,
      context: this.context(),
      value: metric.value,
      delta: metric.delta,
      rating: metric.rating,
      navigationType: metric.navigationType,
      revision: 0,
      lastSentValue: undefined,
    });
  }

  /**
   * Emit every metric that is unsent or has refined since its last emit, each
   * at its next revision, as a single batched beacon. A no-op when nothing is
   * new — so it is safe to call on every lifecycle event.
   */
  flush(): void {
    const reports: WebVitalReport[] = [];

    for (const state of this.states.values()) {
      const refined =
        state.lastSentValue === undefined ||
        state.value !== state.lastSentValue;
      if (!refined) continue;

      reports.push({
        id: state.id,
        revision: state.revision,
        name: state.name,
        value: state.value,
        rating: state.rating,
        delta: state.delta,
        navigationType: state.navigationType,
        route: state.context.route,
        path: state.context.path,
        ts: state.context.ts,
      });

      state.revision += 1;
      state.lastSentValue = state.value;
    }

    if (reports.length > 0) this.send(reports);
  }
}
